import { type ReactNode, useEffect } from 'react';

import type { StripeSubscription } from '@flintmint/core-shared/firestore/schemas';
import { Button } from '@flintmint/web-shared/components/button';

import { useUrlOverlay } from '@/hooks/use-url-overlay';
import { urlOverlays } from '@/constants/url-overlays';
import { useUserAccount } from '@/hooks/use-user-account';
import { FullScreenOverlay, FullScreenOverlayPresence, OverlayNote, OverlayPanel } from '@/components/full-screen-overlay';

import { canCancelSubscription } from '../billing-state';
import { formatBillingDate, formatBillingDayMonth } from '../billing-format';
import { useCancelSubscription } from '../use-cancel-subscription';

import styles from './cancel-subscription.module.scss';

/** Mounted on Subscription & Billing; its Cancel subscription and Cancel Trial buttons open it. */
export const CancelSubscription = () => {
  const { isOpen, close } = useUrlOverlay(urlOverlays.cancelSubscription, urlOverlays);

  return (
    <FullScreenOverlayPresence open={isOpen}>
      <CancelSubscriptionGate onClose={close} />
    </FullScreenOverlayPresence>
  );
};

/**
 * Any account that cannot cancel closes the confirmation unseen. So does a confirmed cancel once the synced
 * subscription shows its scheduled end, revealing it on the page under it.
 */
const CancelSubscriptionGate = ({ onClose }: { onClose: () => void }) => {
  const { account, loading } = useUserAccount();
  const subscription = canCancelSubscription(account) ? account?.stripeSubscription : undefined;
  const denied = !loading && !subscription;

  useEffect(() => {
    if (denied) onClose();
  }, [denied, onClose]);

  return subscription && <CancelSubscriptionDialog subscription={subscription} onClose={onClose} />;
};

/** A failed cancel stays on this screen, as the hook toasts the error. */
const CancelSubscriptionDialog = ({ subscription, onClose }: { subscription: StripeSubscription; onClose: () => void }) => {
  const { cancelSubscription, isPending } = useCancelSubscription();
  // Leaving would hide a cancel that is still being applied.
  const closeUnlessPending = () => {
    if (!isPending) onClose();
  };
  const copy = subscription.status !== 'trialing' ? paidCopy : subscription.pendingInvoiceUrl ? heldTrialCopy : trialCopy;

  return (
    <FullScreenOverlay title={copy.title} subtitle={copy.subtitle(subscription)} onEscape={closeUnlessPending}>
      {copy.body(subscription)}
      <div className={styles.actions}>
        <Button size="big" fullWidth cornerRadius="smooth" color="danger" loading={isPending} onClick={cancelSubscription}>
          {copy.confirmLabel}
        </Button>
        <Button size="big" fullWidth cornerRadius="smooth" appearance="clear" disabled={isPending} onClick={closeUnlessPending}>
          {copy.keepLabel}
        </Button>
      </div>
    </FullScreenOverlay>
  );
};

type DialogCopy = {
  title: string;
  subtitle: (subscription: StripeSubscription) => string;
  body: (subscription: StripeSubscription) => ReactNode;
  confirmLabel: string;
  keepLabel: string;
};

const paidCopy: DialogCopy = {
  title: 'Cancel your subscription?',
  subtitle: ({ currentPeriodEnd }) => `Your paid access continues until ${formatBillingDate(currentPeriodEnd)}.`,
  body: () => (
    <OverlayPanel title="You’ll move to Free">
      After that, non-AI features remain available. Purchased Joules stay saved but require an eligible paid subscription to use.
    </OverlayPanel>
  ),
  confirmLabel: 'Cancel subscription',
  keepLabel: 'Keep subscription',
};

// A Trial's period ends with the Trial.
const trialCopy: DialogCopy = {
  title: 'Cancel scheduled subscription?',
  subtitle: () => 'Keep the rest of your Trial without a scheduled charge.',
  body: ({ currentPeriodEnd }) => (
    <TrialBody
      currentPeriodEnd={currentPeriodEnd}
      paragraphs={[
        `Your paid plan will not start automatically. Keep any remaining Trial Energy and benefits until ${formatBillingDate(
          currentPeriodEnd
        )}.`,
        'Your notes and existing content stay available.',
      ]}
    />
  ),
  confirmLabel: 'Cancel scheduled subscription',
  keepLabel: 'Keep scheduled subscription',
};

// The cancel voids the held early-start invoice before scheduling the end.
const heldTrialCopy: DialogCopy = {
  ...trialCopy,
  body: ({ currentPeriodEnd }) => (
    <TrialBody
      currentPeriodEnd={currentPeriodEnd}
      paragraphs={[
        'The held early-start payment will be cancelled. Your paid plan will not start automatically.',
        `Keep your remaining Trial Energy until ${formatBillingDate(currentPeriodEnd)}. All your work stays.`,
      ]}
    />
  ),
};

const TrialBody = ({ currentPeriodEnd, paragraphs }: { currentPeriodEnd: string; paragraphs: string[] }) => (
  <>
    <OverlayPanel title="Your remaining Trial stays available">
      <div className={styles.paragraphs}>
        {paragraphs.map((paragraph) => (
          <div key={paragraph}>{paragraph}</div>
        ))}
      </div>
    </OverlayPanel>
    <OverlayNote>
      {`After ${formatBillingDayMonth(
        currentPeriodEnd
      )}, keep working on Free. Cancelling does not delete your account or content.`}
    </OverlayNote>
  </>
);
